import type { ColumnDef, HeaderContext } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";

import { AssetLogo } from "@/components/asset/asset-logo";
import { AssetTypeBadge } from "@/components/asset/asset-utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatLargeCurrency } from "@/lib/currency";
import type { Asset } from "@/schemas";

import type { ScreenerScope } from "../utils/screener-preferences";

export interface AssetVariant {
  asset_id: string;
  symbol: string;
  name: string;
  isin?: string;
  currency_symbol: string;
  exchange: string;
  sync: boolean;
  is_primary: boolean;
}

export type ScreenerAsset = Asset & {
  variants?: AssetVariant[];
};

export const SCREENER_COLUMN_LABELS: Record<string, string> = {
  company: "Company",
  sector: "Sector",
  category: "Category",
  market_cap_usd: "Market Cap",
  net_assets: "Net Assets",
  latest_price: "Price",
  daily_change_percent: "Day %",
  trailing_pe: "P/E",
  forward_pe: "Forward P/E",
  peg_ratio: "PEG",
  price_to_book: "P/B",
  latest_price_to_sales: "P/S",
  latest_price_to_earnings: "P/E Valuation",
  latest_price_to_fcf: "P/FCF",
  dividend_yield: "Div Yield",
  yield: "Yield",
  return_on_equity: "ROE",
  return_on_assets: "ROA",
  revenue_growth: "Revenue Growth",
  earnings_quarterly_growth: "Earnings Growth",
  gross_margins: "Gross Margin",
  operating_margins: "Operating Margin",
  profit_margins: "Profit Margin",
  current_ratio: "Current Ratio",
  quick_ratio: "Quick Ratio",
  payout_ratio: "Payout Ratio",
  beta: "Beta",
  short_ratio: "Short Ratio",
  average_volume: "Average Volume",
  float_shares: "Float Shares",
  held_percent_insiders: "Insider %",
  held_percent_institutions: "Institutional %",
  fifty_two_week_high: "52W High",
  fifty_two_week_low: "52W Low",
  fifty_day_average: "50D Avg",
  two_hundred_day_average: "200D Avg",
  expense_ratio: "Expense Ratio",
  ytd_return: "YTD Return",
  three_year_avg_return: "3Y Return",
  five_year_avg_return: "5Y Return",
  beta_3year: "3Y Beta",
  fund_family: "Fund Family",
};

const percentColumns = new Set([
  "daily_change_percent",
  "dividend_yield",
  "yield",
  "return_on_equity",
  "return_on_assets",
  "revenue_growth",
  "earnings_quarterly_growth",
  "gross_margins",
  "operating_margins",
  "profit_margins",
  "payout_ratio",
  "held_percent_insiders",
  "held_percent_institutions",
  "ytd_return",
  "three_year_avg_return",
  "five_year_avg_return",
]);

const largeNumberColumns = new Set([
  "market_cap_usd",
  "net_assets",
  "average_volume",
  "float_shares",
]);

const ratioColumns = new Set([
  "trailing_pe",
  "forward_pe",
  "peg_ratio",
  "price_to_book",
  "latest_price_to_sales",
  "latest_price_to_earnings",
  "latest_price_to_fcf",
  "current_ratio",
  "quick_ratio",
  "beta",
  "short_ratio",
  "beta_3year",
  "expense_ratio",
]);

function formatPercent(value?: number | null): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(2)}%`;
}

function formatNumber(value?: number | null, digits = 2): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value);
}

function formatLargeNumber(value?: number | null): string {
  if (value == null) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return formatNumber(value, 0);
}

function formatCurrency(value?: number | null, currencySymbol?: string): string {
  if (value == null) return "—";
  const symbol = currencySymbol ?? "$";
  return `${symbol}${formatNumber(value, 2)}`;
}

function sortableHeader(label: string) {
  const SortableHeader = ({ column }: HeaderContext<ScreenerAsset, unknown>) => (
    <div className="text-right">
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-8 px-0 justify-end hover:bg-transparent"
      >
        {label}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
  SortableHeader.displayName = `${label}Header`;
  return SortableHeader;
}

function createMetricColumn(id: string): ColumnDef<ScreenerAsset> {
  const label = SCREENER_COLUMN_LABELS[id] ?? id;
  return {
    id,
    accessorKey: id,
    enableHiding: true,
    meta: { columnLabel: label },
    header: sortableHeader(label),
    cell: ({ row }) => {
      const asset = row.original as ScreenerAsset & Record<string, number | string | undefined>;
      const value = asset[id];
      let formatted = "—";

      if (typeof value === "number") {
        if (percentColumns.has(id)) {
          formatted = formatPercent(value);
        } else if (id === "market_cap_usd") {
          formatted = formatLargeCurrency(value, "USD");
        } else if (id === "latest_price") {
          formatted = formatCurrency(value, asset.currency_symbol as string | undefined);
        } else if (largeNumberColumns.has(id)) {
          formatted = formatLargeNumber(value);
        } else if (ratioColumns.has(id)) {
          formatted = formatNumber(value, 2);
        } else {
          formatted = formatNumber(value, 2);
        }
      } else if (typeof value === "string" && value) {
        formatted = value;
      }

      const isDayColumn = id === "daily_change_percent" && typeof value === "number";
      return (
        <div
          className="text-right font-medium"
          style={
            isDayColumn
              ? { color: value >= 0 ? "#10b981" : "#ef4444" }
              : undefined
          }
        >
          {isDayColumn && value >= 0 ? "+" : ""}
          {formatted}
        </div>
      );
    },
  };
}

export function getScreenerColumns(scope: ScreenerScope): ColumnDef<ScreenerAsset>[] {
  const companyColumn: ColumnDef<ScreenerAsset> = {
    id: "company",
    accessorKey: "name",
    enableHiding: false,
    meta: {
      columnLabel: "Company",
      className: "sticky left-0 bg-background z-10 min-w-[260px]",
    },
    header: "Company",
    cell: ({ row }) => {
      const asset = row.original;
      const secondary =
        scope === "etf"
          ? ("category" in asset ? asset.category : undefined)
          : ("sector" in asset ? asset.sector : undefined);
      return (
        <div className="flex items-center gap-3 min-w-[240px]">
          <AssetLogo asset={asset} size="md" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-semibold">
              <span>{asset.symbol}</span>
              <AssetTypeBadge type={asset.type} />
            </div>
            <div className="truncate text-sm text-muted-foreground">{asset.name}</div>
            {secondary && (
              <div className="mt-1">
                <Badge variant="outline" className="font-normal">
                  {secondary}
                </Badge>
              </div>
            )}
          </div>
        </div>
      );
    },
  };

  const stockColumns = [
    "sector",
    "market_cap_usd",
    "latest_price",
    "daily_change_percent",
    "trailing_pe",
    "dividend_yield",
    "return_on_equity",
    "forward_pe",
    "peg_ratio",
    "price_to_book",
    "latest_price_to_sales",
    "latest_price_to_earnings",
    "latest_price_to_fcf",
    "revenue_growth",
    "earnings_quarterly_growth",
    "gross_margins",
    "operating_margins",
    "profit_margins",
    "return_on_assets",
    "current_ratio",
    "quick_ratio",
    "payout_ratio",
    "beta",
    "short_ratio",
    "average_volume",
    "float_shares",
    "held_percent_insiders",
    "held_percent_institutions",
    "fifty_two_week_high",
    "fifty_two_week_low",
    "fifty_day_average",
    "two_hundred_day_average",
  ];

  const etfColumns = [
    "category",
    "net_assets",
    "latest_price",
    "daily_change_percent",
    "yield",
    "expense_ratio",
    "ytd_return",
    "three_year_avg_return",
    "five_year_avg_return",
    "beta_3year",
    "market_cap_usd",
    "fund_family",
    "dividend_yield",
    "average_volume",
  ];

  const ids = scope === "etf" ? etfColumns : stockColumns;
  return [companyColumn, ...ids.map(createMetricColumn)];
}
